/**
 * @module Roles
 * @status PORTED (from Logis lib/constants/roles.ts)
 */

export const USER_ROLES = [
  "admin",
  "dispatcher",
  "accountant",
  "safety",
  "driver",
  "customer",
  "super_admin",
] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrator",
  dispatcher: "Dispatcher",
  accountant: "Accountant",
  safety: "Safety",
  driver: "Driver",
  customer: "Customer",
  super_admin: "Super Admin",
};

/** Sentinel companyId for the singleton platform super admin (not a real tenant). */
export const PLATFORM_COMPANY_ID = "_platform";

/**
 * Nav items visible per role — dashboard routes.
 * Company admin keeps full TMS (`*`) but MUST NOT access `/super` (enforced in canAccessRoute).
 */
export const ROLE_NAV_ACCESS: Record<UserRole, string[]> = {
  admin: ["*"],
  dispatcher: [
    "/",
    "/loads",
    "/dispatch",
    "/trips",
    "/parties",
    "/tracking",
    "/drivers",
    "/calculator",
    "/import",
    "/ai",
    "/docs",
    "/guides",
    "/settings",
  ],
  accountant: [
    "/",
    "/loads",
    "/settlements",
    "/pay-runs",
    "/invoices",
    "/ar",
    "/collections",
    "/factoring",
    "/ap",
    "/ifta",
    "/calculator",
    "/drivers",
    "/qb",
    "/docs",
    "/guides",
    "/settings",
  ],
  safety: ["/", "/fleet", "/drivers", "/docs", "/guides", "/settings"],
  /** Driver login — portal parked until drivers app is live. */
  driver: ["/"],
  /** Customer self-service portal — read-only visibility into their own loads/invoices/tracking. */
  customer: ["/", "/loads", "/invoices", "/tracking", "/docs", "/guides"],
  /** Singleton platform operator — Super Admin console only. */
  super_admin: [
    "/super",
    "/super/audit",
    "/super/users",
    "/super/errors",
    "/super/metrics",
    "/super/security",
    "/super/billing",
    "/super/companies",
    "/super/marketing",
    "/super/apps",
  ],
};

export function canAccessRoute(role: string, href: string): boolean {
  // Super console is exclusive — company admin `*` must never unlock it.
  if (href === "/super" || href.startsWith("/super/")) {
    return role === "super_admin";
  }
  if (role === "super_admin") {
    const access = ROLE_NAV_ACCESS.super_admin;
    return access.some((r) => href === r || (r !== "/" && href.startsWith(r)));
  }
  const access = ROLE_NAV_ACCESS[role as UserRole];
  if (!access) return false;
  if (access.includes("*")) return true;
  return access.some((r) => href === r || (r !== "/" && href.startsWith(r)));
}

export function isStaffRole(role: string): boolean {
  return (
    role === "admin" ||
    role === "dispatcher" ||
    role === "accountant" ||
    role === "safety" ||
    role === "super_admin"
  );
}

export function isSuperAdminRole(role: string): boolean {
  return role === "super_admin";
}

/** Section 1.5 permission matrix helpers */
export type PermissionAction = "full" | "view" | "none";

export const ROLE_PERMISSIONS: Record<
  UserRole,
  Record<"loads" | "dispatch" | "accounting" | "fleet" | "settings" | "reports", PermissionAction>
> = {
  admin: {
    loads: "full",
    dispatch: "full",
    accounting: "full",
    fleet: "full",
    settings: "full",
    reports: "full",
  },
  dispatcher: {
    loads: "full",
    dispatch: "full",
    accounting: "view",
    fleet: "view",
    settings: "none",
    reports: "view",
  },
  accountant: {
    loads: "view",
    dispatch: "none",
    accounting: "full",
    fleet: "view",
    settings: "none",
    reports: "full",
  },
  safety: {
    loads: "view",
    dispatch: "view",
    accounting: "none",
    fleet: "full",
    settings: "none",
    reports: "view",
  },
  driver: {
    loads: "view",
    dispatch: "none",
    accounting: "none",
    fleet: "none",
    settings: "none",
    reports: "none",
  },
  /** Customer portal — can view their own loads/invoices; everything else is none. */
  customer: {
    loads: "view",
    dispatch: "none",
    accounting: "view",
    fleet: "none",
    settings: "none",
    reports: "none",
  },
  super_admin: {
    loads: "none",
    dispatch: "none",
    accounting: "none",
    fleet: "none",
    settings: "view",
    reports: "full",
  },
};
